import React, { useCallback, useRef, useState } from 'react';
import { DragAndDrop, IconSpinner, Modal } from '../../../../components';
import { manageDisableScrolling } from '../../../../ducks/ui.duck';
import { useDispatch } from 'react-redux';
import css from './CreatePostForm.module.css';
import { handleFileUpload } from '../../../../util/circleHelper';
import IconCard from '../../../../components/SavedCardDetails/IconCard/IconCard';
import { getPostMediaCount, validatePostMediaFiles } from './postMediaUpload';

export const generateVideoThumbnail = videoSrc =>
  new Promise(resolve => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    let settled = false;

    const cleanup = () => {
      video.onloadedmetadata = null;
      video.onseeked = null;
      video.onerror = null;
    };

    const finish = value => {
      if (settled) return;
      settled = true;
      clearTimeout(fallbackTimer);
      cleanup();
      resolve(value);
    };

    const fallbackTimer = setTimeout(() => finish(null), 2000);

    const finishWithThumbnail = () => {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      if (!canvas.width || !canvas.height) {
        finish(null);
        return;
      }

      try {
        const context = canvas.getContext('2d');
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        finish(canvas.toDataURL('image/jpeg', 0.8));
      } catch (error) {
        finish(null);
      }
    };

    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    video.onloadedmetadata = () => {
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      if (!duration) {
        finishWithThumbnail();
        return;
      }

      try {
        video.currentTime = Math.min(0.1, duration);
      } catch (error) {
        finishWithThumbnail();
      }
    };

    video.onseeked = finishWithThumbnail;

    video.onerror = () => {
      finish(null);
    };

    video.src = videoSrc;
    video.load();
  });

export const updateUploadedVideoPoster = ({ form, setFormOldValues, signedId, poster }) => {
  if (!poster || !signedId) {
    return;
  }

  const addPoster = videoFile =>
    videoFile?.signed_id === signedId ? { ...videoFile, poster } : videoFile;

  if (setFormOldValues && typeof setFormOldValues === 'function') {
    setFormOldValues(prev => ({
      ...prev,
      videofiles: Array.isArray(prev?.videofiles) ? prev.videofiles.map(addPoster) : [],
    }));
  }

  if (form && form.change) {
    const currentVideoFiles = form.getState?.().values?.videofiles || [];
    form.change('videofiles', currentVideoFiles.map(addPoster));
  }
};

const addUploadedVideo = ({
  form,
  values,
  formOldValues,
  setFormOldValues,
  newVideo,
  newVideoFile,
}) => {
  if (setFormOldValues && typeof setFormOldValues === 'function') {
    setFormOldValues(prev => ({
      ...prev,
      video: [...(prev?.video || []), newVideo],
      videofiles: [...(prev?.videofiles || []), newVideoFile],
    }));
  }

  if (form && form.change) {
    const currentFormValues = form.getState?.().values || values || formOldValues || {};
    const currentVideos = currentFormValues?.video || [];
    const currentVideoFiles = currentFormValues?.videofiles || [];

    form.change('video', [...currentVideos, newVideo]);
    form.change('videofiles', [...currentVideoFiles, newVideoFile]);
  }
};

const FileInput = React.memo(({ inputRef, onChange, isUploadDisabled }) => {
  return (
    <input
      ref={inputRef}
      data-testid="video-file-input"
      type="file"
      accept="video/*"
      onChange={onChange}
      disabled={isUploadDisabled}
      multiple
      style={{ display: 'none' }}
    />
  );
});

const VideoUploadModal = props => {
  const {
    isVideoUpload,
    onVideoClick,
    form,
    values,
    requestDirectUpload,
    formOldValues,
    setFormOldValues,
    isOnline = true,
  } = props;

  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const isLoading = requestDirectUpload || isUploading;
  const isUploadDisabled = !dispatch || isLoading;

  const uploadVideos = useCallback(
    async (selectedFiles, input = null) => {
      const currentFormValues = form?.getState?.().values || values || {};
      const existingCount = Math.max(
        getPostMediaCount(currentFormValues),
        getPostMediaCount(formOldValues)
      );
      const { acceptedFiles, errors: validationErrors } = validatePostMediaFiles({
        files: selectedFiles,
        existingCount,
        allowedKinds: ['video'],
      });

      setUploadError(validationErrors.length ? validationErrors.join(' ') : null);
      if (!acceptedFiles.length) {
        if (input) input.value = '';
        return;
      }

      setIsUploading(true);

      try {
        const results = await Promise.allSettled(
          acceptedFiles.map(file => handleFileUpload(file, dispatch))
        );
        const failedFiles = [];
        let successfulCount = 0;

        results.forEach((result, index) => {
          const file = acceptedFiles[index];
          if (result.status !== 'fulfilled' || !result.value?.signed_id) {
            failedFiles.push(file.name);
            return;
          }

          successfulCount += 1;
          const previewUrl = URL.createObjectURL(file);
          addUploadedVideo({
            form,
            values,
            formOldValues,
            setFormOldValues,
            newVideo: result.value,
            newVideoFile: {
              file: previewUrl,
              poster: null,
              signed_id: result.value.signed_id,
            },
          });

          generateVideoThumbnail(previewUrl)
            .then(poster =>
              updateUploadedVideoPoster({
                form,
                setFormOldValues,
                signedId: result.value.signed_id,
                poster,
              })
            )
            .catch(error => console.error('Video thumbnail generation failed:', error));
        });

        const uploadErrors = [
          ...validationErrors,
          ...(failedFiles.length
            ? [`Failed to upload ${failedFiles.length} file(s): ${failedFiles.join(', ')}.`]
            : []),
        ];
        setUploadError(uploadErrors.length ? uploadErrors.join(' ') : null);

        if (successfulCount > 0 && !uploadErrors.length) {
          onVideoClick();
        }
      } catch (error) {
        console.error('Video upload failed:', error);
        setUploadError('An unexpected error occurred during upload. Please try again.');
      } finally {
        if (input) input.value = '';
        setIsUploading(false);
      }
    },
    [dispatch, form, formOldValues, onVideoClick, setFormOldValues, values]
  );

  const handleFileInputChange = useCallback(
    event => uploadVideos(event.target?.files, event.target),
    [uploadVideos]
  );

  const handleChooseFileClick = useCallback(() => {
    if (!isUploadDisabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [isUploadDisabled]);

  if (!isOnline) {
    return null;
  }

  return (
    <div>
      <Modal
        id={'videoUploadModal'}
        isOpen={isVideoUpload}
        onClose={() => {
          if (!isLoading) {
            onVideoClick();
          }
        }}
        onManageDisableScrolling={manageDisableScrolling}
        className={css.uploadModals}
      >
        <div className={css.contentWrapper}>
          {isLoading ? (
            <div className={css.loader}>
              <IconSpinner type={true} />
            </div>
          ) : (
            <DragAndDrop
              handleDrop={files =>
                uploadVideos(files)
              }
              acceptedFileType={'video'}
            >
              <div className={css.uploadWrapper}>
                <div className={css.headingName}>Add video</div>
                <div className={css.boxUploader}>
                  <div className={css.plusIcon}>
                    <span className={css.icon}>
                      <IconCard brand="upload" />
                    </span>
                  </div>
                  <div className={css.dragText}>Drop your videos here to upload</div>
                  <div className={css.fileTypes}>
                    Videos up to 100 MB. Maximum 10 media files per post.
                  </div>

                  <button
                    type="button"
                    className={css.chooseFileButton}
                    onClick={handleChooseFileClick}
                    disabled={isUploadDisabled}
                  >
                    Choose videos
                  </button>
                  <FileInput
                    inputRef={fileInputRef}
                    onChange={handleFileInputChange}
                    isUploadDisabled={isUploadDisabled}
                  />
                </div>
                {uploadError ? <div className={css.errorMessage}>{uploadError}</div> : null}
              </div>
            </DragAndDrop>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default VideoUploadModal;
